import { useMemo } from 'react';
import { Modal, Notice, Spinner } from '../ui';
import { Link } from '../router/Link';
import { describeAuthProblem } from '../api/problem';
import { CreatePlaylistForm } from './CreatePlaylistForm';
import { ownedPlaylistsProblem, signInHereHref } from './model';
import { chooseWatchLater, type WatchLaterChoice } from './useWatchLater';
import { useOwnedPlaylists } from './useOwnedPlaylists';
import { watchLaterPickerRows } from './watchLaterModel';
import { useCopy } from '../strings/useCopy';
import styles from './addToPlaylist.module.css';

/**
 * The one dialog that appoints the Watch later playlist (2026-08-02) — opened by the settings
 * row, and by any Watch later button pressed before a playlist is appointed. `AddToPlaylist`'s
 * clothes on purpose (its stylesheet, its row shape, its create form at the bottom): both dialogs
 * answer "which of your playlists", and a reader who has seen one has seen the other.
 *
 * Picking **writes the setting here** (`chooseWatchLater`) and then reports the choice up — the
 * callers differ in what happens next (the settings row is done; a button goes on to add its
 * video), not in what choosing means.
 */
export function WatchLaterPicker({
  open,
  current,
  signedIn,
  onClose,
  onPicked,
}: {
  readonly open: boolean;
  /** The already-appointed playlist, so its row is marked. */
  readonly current: WatchLaterChoice | null;
  readonly signedIn: boolean;
  readonly onClose: () => void;
  /** Fires after the setting is written. */
  readonly onPicked: (choice: WatchLaterChoice) => void;
}) {
  const owned = useOwnedPlaylists(open && signedIn);
  const words = useCopy().text.playlists.watchLater;

  const rows = useMemo(
    () => watchLaterPickerRows(owned.playlists, current?.playlistId ?? null),
    [current?.playlistId, owned.playlists],
  );

  const loadProblem = useMemo(
    () => (owned.problem === null ? null : describeAuthProblem(owned.problem, ownedPlaylistsProblem())),
    [owned.problem],
  );

  const pick = (choice: WatchLaterChoice): void => {
    chooseWatchLater(choice);
    onPicked(choice);
  };

  return (
    <Modal open={open} onClose={onClose} title={words.pickerTitle} closeLabel={words.close}>
      {!signedIn ? (
        <p className={styles.detail}>
          {words.signedOutBefore}{' '}
          <Link className={styles.link} href={signInHereHref()}>
            {words.signIn}
          </Link>{' '}
          {words.signedOutAfter}
        </p>
      ) : (
        <div className={styles.content}>
          <p className={styles.detail}>{words.pickerLede}</p>

          {owned.loading ? (
            <div className={styles.detail} role="status">
              <Spinner />
              <span>{words.loading}</span>
            </div>
          ) : null}

          {loadProblem === null ? null : (
            <Notice tone="caution">
              <strong>{loadProblem.title}.</strong> {loadProblem.detail}
            </Notice>
          )}

          {rows !== null && rows.length === 0 ? (
            <p className={styles.detail}>{words.none}</p>
          ) : null}

          {rows === null || rows.length === 0 ? null : (
            <ul className={styles.list}>
              {rows.map((row) => (
                <li className={styles.row} key={row.playlistId}>
                  <button
                    type="button"
                    className={styles.rowButton}
                    onClick={() => pick({ playlistId: row.playlistId, title: row.title })}
                  >
                    <span className={styles.rowTitle}>{row.title}</span>
                    <span className={styles.rowMeta}>
                      {row.countText === null ? null : <span>{row.countText}</span>}
                    </span>
                    <span className={styles.rowState}>
                      {row.current ? <span className={styles.added}>{words.current}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <CreatePlaylistForm
            id="watch-later-picker-create"
            submitLabel={words.createSubmit}
            onCreated={(playlistId, title) => pick({ playlistId, title })}
          />
        </div>
      )}
    </Modal>
  );
}
